import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Loader2Icon } from 'lucide-react';
import CheckIdentity from './CheckIdentity';
import CyberViewContainer from './components/CyberViewContainer';
import FormContainer from './components/FormContainer';
import RegisterHeaderComponent from './components/RegisterHeaderComponent';
import FormSummary from './FormSummary';
import OtpComponent from './OtpComponent';
import PaymentStep from './PaymentStep';
import PersonalInfo, {
	PersonalInfoFormData,
	QUERY_SIGNATURE_KEY,
	QUERY_SIGNATURE_NEW,
} from './PersonalInfo';
import RegisterStart from './RegisterStart';
import OnboardingInReview from './OnboardingInReview';
import ResumeOnboardingChoice from './ResumeOnboardingChoice';
import ScrollableAgreement from './ScrollableAgreement';
import {
	clampBackStep,
	clearOnboardingStorage,
	ONBOARDING_STEP_CACHE_KEY,
	raiseFloor,
} from './onboarding-resume';
import { useOnboardingResume } from './use-onboarding-resume';
import { useAuthContext } from '@/components/auth-context/auth-context';
import { toast } from '@/components/toast/toast';
import notifyHB from '@/config/notifyHB';
import routeConfig from '@/config/routeConfig';
import { Certificate } from '@/entities/certificate';
import { OnboardingData } from '@/use-cases/data-enrichment';
import {
	getOnboardingDestination,
	getOnboardingFloor,
	OnboardingStatus,
	WIZARD_STEP,
} from '@/entities/onboarding-status';
import { abandonOnboarding } from '@/use-cases/abandon-onboarding';
import { isOnboardingConflict } from '@/utils/is-onboarding-conflict';
import { trackEvent } from '@/utils/analytics';

const RequestSignature = () => {
	const { userProfile, kycUrl } = useAuthContext();
	const history = useHistory();
	const location = useLocation();
	const [isNewSignatureRequest] = useState(
		() =>
			new URLSearchParams(location.search).get(QUERY_SIGNATURE_KEY) ===
			QUERY_SIGNATURE_NEW,
	);
	const { entry, status, refresh, resolve } = useOnboardingResume(
		isNewSignatureRequest,
	);
	// null until the backend status decides where the wizard opens.
	const [componentState, setComponentState] = useState<number | null>(null);
	const [floor, setFloor] = useState<number>(WIZARD_STEP.START);
	const [sendOtpOnMount, setSendOtpOnMount] = useState(false);
	const [isAbandoning, setIsAbandoning] = useState(false);
	// Set when the backend reports IN_REVIEW: the wizard shows the request
	// instead of any step.
	const [reviewStatus, setReviewStatus] = useState<OnboardingStatus | null>(
		null,
	);
	const [personName, setPersonName] = useState('');
	const [address, setAddress] = useState('');
	const [nit, setNit] = useState('');
	const [documentNumber, setDocumentNumber] = useState('');
	const [userEmail, setUserEmail] = useState('');
	const [userPhoneCountryCode, setUserPhoneCountryCode] = useState('');
	const [userPhone, setUserPhone] = useState('');
	const [birthDate, setBirthDate] = useState('');
	const [documentType, setDocumentType] =
		useState<OnboardingData['document_type']>();
	const [certificateType, setCertificateType] =
		useState<Certificate['type']>();
	const [additionalData, setAdditionalData] =
		useState<PersonalInfoFormData>();
	const [selectedProductId, setSelectedProductId] = useState<
		string | undefined
	>();
	const kycStartedTrackedRef = useRef(false);

	useEffect(() => {
		if (!userProfile) return;
		setPersonName(userProfile.FullName ?? '');
		setNit(userProfile.NIT ?? '');
		setAddress(userProfile.Address);
		setUserEmail(userProfile.Email);
		setUserPhoneCountryCode(userProfile.PhoneCountryCode);
		setUserPhone(userProfile.PhoneNumber);
		setDocumentNumber(userProfile.DocumentNumber);
		setBirthDate(userProfile.BirthDate);
	}, [userProfile]);

	// Only the steps the backend reports as FORM are cached; later steps
	// always come from the status endpoint.
	useEffect(() => {
		if (componentState !== null && componentState <= WIZARD_STEP.FORM) {
			localStorage.setItem(
				ONBOARDING_STEP_CACHE_KEY,
				String(componentState),
			);
		}
	}, [componentState]);

	const enterStep = (step: number, stepFloor: number) => {
		setFloor((currentFloor) => Math.max(currentFloor, stepFloor));
		// A resumed SMS step has no code in flight: the summary that sends it
		// was skipped.
		setSendOtpOnMount(step === WIZARD_STEP.SMS_VERIFICATION);
		setComponentState(step);
	};

	/** Continues the process `ulid` from the wizard step the backend reports. */
	const resumeProcess = (ulid: string, step: number, stepFloor: number) => {
		localStorage.setItem('onboarding-ulid', ulid);
		// ?signature=new would send a later form submission down the
		// new-certificate branch, so drop it once a process is resumed.
		if (history.location.search) {
			history.replace(history.location.pathname);
		}
		enterStep(step, stepFloor);
	};

	/** Re-reads the status after a 409 and jumps to the step it reports. */
	const resyncWithBackend = async () => {
		const refreshed = await resolve();
		if (!refreshed) {
			toast({
				mode: 'destructive',
				description:
					'Ocurrió un error inesperado. Intenta de nuevo más tarde',
			});
			return;
		}
		const destination = getOnboardingDestination(refreshed.step);
		if (destination.kind === 'dashboard') {
			history.replace(routeConfig.dashboard);
		} else if (destination.kind === 'in_review') {
			setReviewStatus(refreshed);
		} else if (destination.kind === 'wizard') {
			resumeProcess(
				refreshed.onboarding_ulid,
				destination.step,
				getOnboardingFloor(refreshed.step),
			);
		} else {
			notifyHB(
				'request-signature',
				'RequestSignature',
				new Error(`Unknown onboarding step: ${refreshed.step}`),
			);
		}
	};

	useEffect(() => {
		if (!entry) return;
		switch (entry.kind) {
			case 'dashboard':
				history.replace(routeConfig.dashboard);
				break;
			case 'in_review':
				setReviewStatus(status);
				break;
			case 'start':
				setComponentState(entry.step);
				break;
			case 'resume':
				if (status) {
					resumeProcess(
						status.onboarding_ulid,
						entry.step,
						entry.floor,
					);
				}
				break;
			case 'choose':
				// ResumeOnboardingChoice renders until the user picks.
				break;
		}
	}, [entry]);

	const startNewProcess = async () => {
		if (!status) return;
		setIsAbandoning(true);
		try {
			await abandonOnboarding(status.onboarding_ulid);
			clearOnboardingStorage();
			history.replace(
				`${routeConfig.requestSignature}?${QUERY_SIGNATURE_KEY}=${QUERY_SIGNATURE_NEW}`,
			);
			setFloor(WIZARD_STEP.START);
			setComponentState(WIZARD_STEP.START);
		} catch (error) {
			if (isOnboardingConflict(error)) {
				await resyncWithBackend();
			} else {
				toast({
					mode: 'destructive',
					description:
						'No pudimos iniciar una nueva solicitud. Intenta de nuevo más tarde',
				});
				notifyHB('request-signature', 'RequestSignature', error);
			}
		} finally {
			setIsAbandoning(false);
		}
	};

	/**
	 * Leaves the request in review untouched (no abandon) and runs today's
	 * ?signature=new flow, which creates a separate process.
	 */
	const startNewBesideReview = () => {
		history.replace(
			`${routeConfig.requestSignature}?${QUERY_SIGNATURE_KEY}=${QUERY_SIGNATURE_NEW}`,
		);
		setReviewStatus(null);
		setFloor(WIZARD_STEP.START);
		setComponentState(WIZARD_STEP.START);
	};

	const step = componentState ?? WIZARD_STEP.START;
	const canGoBack = step > floor;

	const goTo = (target: number) => {
		setSendOtpOnMount(false);
		setComponentState((current) =>
			current === null ? current : clampBackStep(target, floor, current),
		);
	};

	const prevStep = () => goTo(step - 1);

	const nextStep = () => {
		const next = step + 1;
		setSendOtpOnMount(false);
		setComponentState(next);
		refresh().then((refreshed) => {
			if (refreshed) {
				setFloor((currentFloor) =>
					raiseFloor(currentFloor, next, refreshed.step),
				);
			}
		});
	};

	const startOnboarding = () => {
		trackEvent('onboarding_started');
		nextStep();
	};

	const completeForm = () => {
		trackEvent('onboarding_form_completed');
		nextStep();
	};

	// CheckIdentity shows the QR (or the mobile link) as soon as kycUrl is set.
	useEffect(() => {
		if (componentState !== 6 || !kycUrl || kycStartedTrackedRef.current) {
			return;
		}
		kycStartedTrackedRef.current = true;
		trackEvent('onboarding_kyc_started');
	}, [componentState, kycUrl]);

	if (reviewStatus) {
		return (
			<CyberViewContainer>
				<FormContainer>
					<OnboardingInReview
						status={reviewStatus}
						onStartNew={startNewBesideReview}
						onBack={() => history.push(routeConfig.dashboard)}
					/>
				</FormContainer>
			</CyberViewContainer>
		);
	}

	if (componentState === null) {
		return (
			<CyberViewContainer>
				<FormContainer>
					{entry?.kind === 'choose' ? (
						<ResumeOnboardingChoice
							step={entry.step}
							isLoading={isAbandoning}
							onResume={() =>
								status &&
								resumeProcess(
									status.onboarding_ulid,
									entry.step,
									entry.floor,
								)
							}
							onStartNew={startNewProcess}
						/>
					) : (
						<div
							data-testid="onboarding-loading"
							style={{
								display: 'flex',
								justifyContent: 'center',
							}}
						>
							<Loader2Icon className="animate-spin" />
						</div>
					)}
				</FormContainer>
			</CyberViewContainer>
		);
	}

	let stepComponent;

	switch (componentState) {
		case 0:
			stepComponent = <RegisterStart nextStep={startOnboarding} />;
			break; // step 0
		case 1:
			stepComponent = <ScrollableAgreement nextStep={nextStep} />;
			break; // step 1
		case 2:
			stepComponent = (
				<PersonalInfo
					nextStep={completeForm}
					setPersonName={setPersonName}
					setNit={setNit}
					setDocumentNumber={setDocumentNumber}
					address={address}
					setAddress={setAddress}
					setUserNumber={setUserPhone}
					setUserPhoneCountryCode={setUserPhoneCountryCode}
					initialDocumentNumber={documentNumber}
					initialNit={nit}
					setInitialBirthDate={birthDate}
					setInitialPhoneCountryCode={userPhoneCountryCode}
					setInitialPhoneNumber={userPhone}
					setCertificateType={setCertificateType}
					setAdditionalData={setAdditionalData}
					setSelectedProductId={setSelectedProductId}
					setBirthDate={setBirthDate}
					documentType={documentType}
					setDocumentType={setDocumentType}
					initialCertificateType={certificateType}
					initialAdditionalData={additionalData}
					initialSelectedProductId={selectedProductId}
					onConflict={resyncWithBackend}
				/>
			);
			break; // step 2 (previously step 1)
		case 3:
			stepComponent = (
				<FormSummary
					nextStep={nextStep}
					prevStep={prevStep}
					personName={personName}
					address={address}
					userPhoneCountryCode={userPhoneCountryCode}
					userPhone={userPhone}
					nit={nit}
					userEmail={userEmail}
					documentNumber={documentNumber}
					setComponentState={goTo}
					certificateType={certificateType}
					additionalData={additionalData}
				/>
			);

			break; // step 3
		case 4:
			stepComponent = (
				<OtpComponent
					prevStep={prevStep}
					nextStep={nextStep}
					sendOtpOnMount={sendOtpOnMount}
				/>
			);
			break; // step 4
		case 5:
			stepComponent = (
				<PaymentStep
					nextStep={nextStep}
					prevStep={prevStep}
					canGoBack={canGoBack}
					selectedProductId={selectedProductId}
					refreshStatus={refresh}
					onConflict={resyncWithBackend}
				/>
			);
			break;
		case 6:
			stepComponent = (
				<CheckIdentity prevStep={prevStep} canGoBack={canGoBack} />
			);
			break; // step 6
		default:
			stepComponent = <RegisterStart nextStep={startOnboarding} />;
			break;
	}

	return (
		<div>
			{componentState === 0 ? (
				stepComponent
			) : (
				<CyberViewContainer>
					<RegisterHeaderComponent
						componentState={componentState}
						canGoBack={canGoBack}
						onBack={prevStep}
					/>
					<FormContainer>{stepComponent}</FormContainer>
				</CyberViewContainer>
			)}
		</div>
	);
};

export default RequestSignature;
